// Tiny wrapper around the Vibration API for subtle haptic feedback on touch.
//
// Reality check: this only does anything on Android (Chrome / Firefox / Samsung
// Internet). iOS Safari — and therefore every browser on iPhone/iPad, since they
// all use WebKit — does not support the Vibration API, so these calls are no-ops
// there. Desktop exposes the API but has no hardware. Treat haptics purely as a
// progressive enhancement: it must never be required feedback and must never throw.
//
// Usage:
//   import { haptics } from '~/common/helpers/haptics'
//   haptics.tick()    // discrete step / gesture start — the workhorse
//   haptics.tap()     // confirm a completed action (copy, add to cart, code entered)
//   haptics.reject()  // blocked / at a limit / error (hit min/max, copy failed)

// Vibration patterns, in milliseconds. Exported for testing.
export const PATTERNS = {
  tick: 10, // light single pulse
  tap: 20, // firmer single pulse — a positive "done"
  reject: [30, 20, 30], // double buzz — "no"
}

const supported = () =>
  typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'

// Honor the user's reduced-motion preference — haptics are non-essential feedback,
// and there is no dedicated "reduced haptics" media query, so this is the accepted proxy.
const reducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Pure decision: which pattern (if any) should fire for a given kind and set of
// capabilities. Returns null when nothing should fire. Kept side-effect-free so
// the gating logic is unit-testable without a DOM.
export function resolveVibration(kind, { supported, reducedMotion } = {}) {
  if (!supported || reducedMotion) return null
  return PATTERNS[kind] ?? null
}

function fire(kind) {
  const pattern = resolveVibration(kind, {
    supported: supported(),
    reducedMotion: reducedMotion(),
  })
  if (pattern == null) return false

  try {
    return navigator.vibrate(pattern)
  } catch {
    return false
  }
}

export const haptics = {
  tick: () => fire('tick'),
  tap: () => fire('tap'),
  reject: () => fire('reject'),
}

export default haptics
